import { Box, Flex } from "@chakra-ui/layout";
import { Text } from "@chakra-ui/react";
import { TipoDestinoEnum } from "core/features/documentos/enums";
import { Destino } from "core/features/mensagens/typings";
import { TipoEntidadeEnum } from "core/resources/enums/conta";
import React, { useEffect, useMemo } from "react";
import { Control, UseFormGetValues, UseFormSetValue } from "react-hook-form";
import Field from "../Input/Field";
import SelectTipoDestino from "../Input/SelectTipoDestino";
import SelectMultipleOptionDisplay from "../SelectMultipleOptionDisplay";
import RenderSelectEntidade from "./RenderSelectEntidade";

interface SelectDestinosProps {
  tenantId: string;
  control: Control<
    { tipoDestinoId: string; destinos: Destino[] } & any,
    object
  >;
  form: {
    tipoDestinoId: string;
    destinos: Destino[];
  } & Record<string, any>;
  setValue: UseFormSetValue<
    { tipoDestinoId: string; destinos: Destino[] } & Record<string, any>
  >;
  getValues: UseFormGetValues<
    { tipoDestinoId: string; destinos: Destino[] } & Record<string, any>
  >;
  selectSize?: "large" | "small";
  inputSize?: "sm" | "md" | "lg" | "xs";
  showEmail?: boolean;
  showPessoa?: boolean;
  showCargo?: boolean;
  label?: string;
  tiposPessoa?: TipoEntidadeEnum[];
}

function SelectDestinos({
  tenantId,
  control,
  form,
  setValue,
  getValues,
  selectSize = "large",
  inputSize = "md",
  showEmail = false,
  showPessoa = false,
  showCargo = false,
  label = "Para: ",
  tiposPessoa,
}: SelectDestinosProps) {
  const onPushDestino = (destino: Destino) => {
    const destinos = getValues("destinos");

    if (
      destinos.findIndex((d) => d.destinoId === destino.destinoId) === -1 &&
      destino.label &&
      destino.destinoId
    ) {
      setValue("destinos", [...destinos, destino]);
    }
  };

  const onRemoveDestino = (id: string) => {
    const destinos = getValues("destinos");

    setValue(
      "destinos",
      destinos.filter((d) => d.destinoId !== id)
    );
  };

  const onRemoveAllDestinos = () => {
    setValue("destinos", []);
  };

  const onPushEmail = (email: string) => {
    const emails = email.split(";");
    emails.forEach((_email) =>
      _email
        ? onPushDestino({
            tipoDestinoId: TipoDestinoEnum.Email,
            destinoId: _email.trim(),
            label: _email.trim(),
          })
        : null
    );
  };

  const destinosIds = useMemo(
    () => form.destinos.map((d) => d.destinoId),
    [form.destinos]
  );

  useEffect(() => {
    setValue("destinoId", undefined);

    if (form.tipoDestinoId === TipoDestinoEnum.TodasUnidades) {
      const destinos = getValues("destinos");

      setValue(
        "destinos",
        destinos.filter((d) => d.tipoDestinoId === TipoDestinoEnum.Email)
      );
    }
  }, [form.tipoDestinoId, getValues, setValue]);

  return (
    <>
      <Field
        name="destinos"
        control={control}
        rules={{
          validate: (v) =>
            form.tipoDestinoId === TipoDestinoEnum.TodasUnidades || v.length > 0
              ? true
              : "Deve escolher pelo menos um destino",
        }}
        render={({ field }) =>
          form.destinos?.length ? (
            <Box w="100%">
              <Text as="h4" fontSize={14} fontWeight="500" mb="4px">
                Destinos
              </Text>
              <SelectMultipleOptionDisplay
                options={field.value.map((d: Destino) => ({
                  id: d.destinoId,
                  label: d.label,
                }))}
                onRemove={onRemoveDestino}
                onRemoveAll={onRemoveAllDestinos}
              />
            </Box>
          ) : (
            <></>
          )
        }
      />

      <Flex alignItems="center" w="100%">
        <Box mr={3}>
          <Text fontSize={14}>{label}</Text>
        </Box>
        <Flex mr={3} w="30%">
          <Field
            name="tipoDestinoId"
            control={control}
            render={({ field, isInvalid }) => (
              <SelectTipoDestino
                onChange={(v) => {
                  field.onChange(v);
                  setValue("destinoId", undefined);
                }}
                value={field.value}
                isInvalid={isInvalid}
                size={selectSize}
                showEmail={showEmail}
                showPessoa={showPessoa}
                showCargo={showCargo}
              />
            )}
          />
        </Flex>
        {form.tipoDestinoId !== TipoDestinoEnum.TodasUnidades ? (
          <Flex flex={1}>
            <Field
              shouldUnregister
              control={control}
              name="destinoId"
              render={({ field, isInvalid }) => (
                <RenderSelectEntidade
                  tenantId={tenantId}
                  field={field}
                  isInvalid={isInvalid}
                  onChangeCargo={(v, option: any) => {
                    onPushDestino({
                      tipoDestinoId: TipoDestinoEnum.UmCargo,
                      destinoId: v,
                      label: option.label,
                    });
                  }}
                  onChangeUnidade={(v, option) => {
                    onPushDestino({
                      tipoDestinoId: TipoDestinoEnum.UmaUnidade,
                      destinoId: v,
                      label: option.label,
                    });
                  }}
                  onChangeGrupo={(v, option) => {
                    onPushDestino({
                      tipoDestinoId: TipoDestinoEnum.UmGrupoUnidades,
                      destinoId: v,
                      label: option.label,
                    });
                  }}
                  onChangePessoa={(v, option: any) => {
                    onPushDestino({
                      tipoDestinoId: TipoDestinoEnum.UmaPessoa,
                      destinoId: v,
                      label: option.label,
                    });
                  }}
                  tipoDestinoId={form.tipoDestinoId as TipoDestinoEnum}
                  selectSize={selectSize}
                  inputSize={inputSize}
                  tiposPessoa={tiposPessoa}
                  onSubmitEmail={onPushEmail}
                  filteredOptions={destinosIds}
                />
              )}
            />
          </Flex>
        ) : null}
      </Flex>
    </>
  );
}

export default SelectDestinos;
